import React, { useState, useEffect } from "react";
import {
  Gift,
  Send,
  Coins,
  Users,
  TrendingUp,
  CheckCircle2,
  Loader2,
  RefreshCw,
  User,
  Mail,
  Phone,
  DollarSign,
  Home,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { getStoredTokens } from "../api/client";

// Use environment variable for API URL; fallback to localhost for local development
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8000'
    : 'https://api.cloudmortgage.io'
);

const getAuthToken = () => {
  const tokens = getStoredTokens();
  return tokens?.accessToken || null;
};

// Milestone bonus configuration
const MILESTONES = [
  { leads: 5, bonus: 10000, label: "5 Leads", icon: "🎯" },
  { leads: 10, bonus: 25000, label: "10 Leads", icon: "🔥" },
  { leads: 20, bonus: 50000, label: "20 Leads", icon: "⭐" },
  { leads: 50, bonus: 150000, label: "50 Leads", icon: "🏆" }
];

const getNextMilestone = (currentLeads) => {
  return MILESTONES.find(m => currentLeads < m.leads);
};

const getAchievedMilestones = (currentLeads) => {
  return MILESTONES.filter(m => currentLeads >= m.leads);
};

/**
 * Submit a lead and earn credits
 */
async function submitLead(leadData, userEmail) {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}/api/referral/submit-lead`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-User-Email': userEmail
    },
    body: JSON.stringify(leadData)
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to submit lead');
  }
  return response.json();
}

/**
 * Fetch submitted leads for user
 */
async function getSubmittedLeads(userEmail) {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}/api/referral/my-leads`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-User-Email': userEmail
    }
  });
  if (!response.ok) throw new Error('Failed to fetch leads');
  return response.json();
}

/**
 * Fetch referral stats for user
 */
async function getReferralStats(userEmail) {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}/api/referral/stats`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-User-Email': userEmail
    }
  });
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
}

const MilestoneProgress = ({ currentLeads }) => {
  const nextMilestone = getNextMilestone(currentLeads);
  const achievedMilestones = getAchievedMilestones(currentLeads);
  const totalBonusEarned = achievedMilestones.reduce((sum, m) => sum + m.bonus, 0);

  return (
    <div className="rounded-2xl border dark:border-slate-700 border-slate-200 dark:bg-gradient-to-br dark:from-slate-800 dark:to-slate-800/50 bg-gradient-to-br from-amber-50 to-white p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2">
          <Gift className="h-5 w-5 text-amber-500" />
          Monthly Milestones
        </h3>
        {totalBonusEarned > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Coins className="h-4 w-4" />
            <span className="text-sm font-semibold">+{totalBonusEarned.toLocaleString()} bonus</span>
          </div>
        )}
      </div>

      {/* Progress Bar to Next Milestone */}
      {nextMilestone && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm dark:text-slate-400 text-slate-500">
              Progress to {nextMilestone.label}
            </span>
            <span className="text-sm font-medium dark:text-white text-slate-900">
              {currentLeads} / {nextMilestone.leads}
            </span>
          </div>
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((currentLeads / nextMilestone.leads) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs dark:text-slate-500 text-slate-400 mt-2">
            {nextMilestone.leads - currentLeads} more lead{nextMilestone.leads - currentLeads !== 1 ? 's' : ''} to unlock <span className="font-semibold text-amber-600 dark:text-amber-400">+{nextMilestone.bonus.toLocaleString()} bonus credits</span>
          </p>
        </div>
      )}

      {/* Milestone Cards */}
      <div className="grid grid-cols-4 gap-3">
        {MILESTONES.map((milestone) => {
          const isAchieved = currentLeads >= milestone.leads;
          const isCurrent = nextMilestone?.leads === milestone.leads;

          return (
            <div
              key={milestone.leads}
              className={`relative p-3 rounded-xl text-center transition-all ${
                isAchieved
                  ? 'bg-emerald-500/10 border-2 border-emerald-500/30'
                  : isCurrent
                  ? 'bg-amber-500/10 border-2 border-amber-500/30 border-dashed'
                  : 'bg-slate-100 dark:bg-slate-800/50 border-2 border-transparent'
              }`}
            >
              <div className="text-2xl mb-1">{milestone.icon}</div>
              <div className={`text-sm font-semibold ${isAchieved ? 'text-emerald-600 dark:text-emerald-400' : 'dark:text-slate-300 text-slate-700'}`}>
                {milestone.label}
              </div>
              <div className={`text-xs ${isAchieved ? 'text-emerald-500' : 'dark:text-slate-500 text-slate-400'}`}>
                +{(milestone.bonus / 1000).toFixed(0)}K credits
              </div>
              {isAchieved && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-3 w-3 text-white" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* All Milestones Achieved */}
      {!nextMilestone && (
        <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-emerald-500/20 border border-amber-500/30 text-center">
          <span className="text-2xl">🎉</span>
          <p className="text-sm font-semibold dark:text-white text-slate-900 mt-1">
            All milestones achieved this month!
          </p>
          <p className="text-xs dark:text-slate-400 text-slate-500">
            You're a top performer! Keep submitting leads.
          </p>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subtext, color = "sky" }) => {
  const colorClasses = {
    sky: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400"
  };

  return (
    <div className="rounded-2xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white p-5">
      <div className="flex items-center gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm dark:text-slate-400 text-slate-500">{label}</p>
          <p className="text-2xl font-bold dark:text-white text-slate-900">{value}</p>
          {subtext && <p className="text-xs dark:text-slate-500 text-slate-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );
};

const LeadRow = ({ lead }) => {
  return (
    <tr className="dark:hover:bg-slate-800/50 hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3">
        <div>
          <p className="font-medium dark:text-white text-slate-900">
            {lead.firstName} {lead.lastName}
          </p>
          <p className="text-sm dark:text-slate-400 text-slate-500">{lead.email}</p>
        </div>
      </td>
      <td className="px-4 py-3 dark:text-slate-300 text-slate-700">
        {lead.phone}
      </td>
      <td className="px-4 py-3 dark:text-slate-300 text-slate-700">
        ${(lead.loanAmount || 0).toLocaleString()}
      </td>
      <td className="px-4 py-3 dark:text-slate-300 text-slate-700">
        {new Date(lead.submittedAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
          <Coins className="h-4 w-4" />
          +5,000
        </span>
      </td>
    </tr>
  );
};

const ReferAndEarn = ({ userEmail }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ totalLeads: 0, creditsEarned: 0, thisMonth: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state - Multi-step form matching RapidRates
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Contact Info
    fullName: '',
    email: '',
    phone: '',
    // Step 2: Loan Details
    occupancyType: 'primary',
    propertyType: 'single_family',
    loanPurpose: 'purchase',
    purchasePrice: '',
    downPayment: '',
    downPaymentPercent: 20,
    city: '',
    state: '',
    zipCode: '',
    creditScore: '740-759',
    isVeteran: 'no',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadData();
  }, [userEmail]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [leadsData, statsData] = await Promise.all([
        getSubmittedLeads(userEmail).catch(() => ({ leads: [] })),
        getReferralStats(userEmail).catch(() => ({ totalLeads: 0, creditsEarned: 0, thisMonth: 0 }))
      ]);
      setLeads(leadsData.leads || []);
      setStats(statsData);
    } catch (err) {
      // Use empty data on error
      setLeads([]);
      setStats({ totalLeads: 0, creditsEarned: 0, thisMonth: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep1 = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};
    if (!formData.purchasePrice) errors.purchasePrice = 'Purchase price is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.zipCode.trim()) errors.zipCode = 'Zip code is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setFormStep(2);
    }
  };

  const handlePrevStep = () => {
    setFormStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Parse name into first/last
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const result = await submitLead({
        firstName,
        lastName,
        email: formData.email,
        phone: formData.phone,
        occupancyType: formData.occupancyType,
        propertyType: formData.propertyType,
        loanPurpose: formData.loanPurpose,
        loanAmount: parseFloat(formData.purchasePrice.replace(/[^0-9.]/g, '')),
        downPayment: parseFloat(formData.downPayment.replace(/[^0-9.]/g, '') || 0),
        downPaymentPercent: formData.downPaymentPercent,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        creditScore: formData.creditScore,
        isVeteran: formData.isVeteran === 'yes',
        notes: formData.notes
      }, userEmail);

      setSuccess(`Lead submitted successfully! You earned ${result.creditsAwarded?.toLocaleString() || '5,000'} credits.`);

      // Reset form
      setFormStep(1);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        occupancyType: 'primary',
        propertyType: 'single_family',
        loanPurpose: 'purchase',
        purchasePrice: '',
        downPayment: '',
        downPaymentPercent: 20,
        city: '',
        state: '',
        zipCode: '',
        creditScore: '740-759',
        isVeteran: 'no',
        notes: ''
      });

      // Refresh data
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to submit lead. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Calculate down payment when purchase price or percentage changes
  const handlePurchasePriceChange = (value) => {
    const formatted = formatCurrency(value);
    const numericPrice = parseFloat(formatted.replace(/[^0-9.]/g, '') || 0);
    const downPaymentAmount = Math.round(numericPrice * (formData.downPaymentPercent / 100));
    setFormData(prev => ({
      ...prev,
      purchasePrice: formatted,
      downPayment: downPaymentAmount > 0 ? formatCurrency(downPaymentAmount.toString()) : ''
    }));
  };

  const handleDownPaymentPercentChange = (percent) => {
    const numericPrice = parseFloat(formData.purchasePrice.replace(/[^0-9.]/g, '') || 0);
    const downPaymentAmount = Math.round(numericPrice * (percent / 100));
    setFormData(prev => ({
      ...prev,
      downPaymentPercent: percent,
      downPayment: downPaymentAmount > 0 ? formatCurrency(downPaymentAmount.toString()) : ''
    }));
  };

  const handleDownPaymentChange = (value) => {
    const formatted = formatCurrency(value);
    const numericDown = parseFloat(formatted.replace(/[^0-9.]/g, '') || 0);
    const numericPrice = parseFloat(formData.purchasePrice.replace(/[^0-9.]/g, '') || 0);
    const percent = numericPrice > 0 ? Math.round((numericDown / numericPrice) * 100) : 0;
    setFormData(prev => ({
      ...prev,
      downPayment: formatted,
      downPaymentPercent: percent
    }));
  };

  const formatCurrency = (value) => {
    const num = String(value).replace(/[^0-9]/g, '');
    if (!num) return '';
    return parseInt(num).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold dark:text-white text-slate-900 flex items-center gap-3">
            <Gift className="h-7 w-7 text-sky-500" />
            Refer & Earn
          </h1>
          <p className="mt-1 dark:text-slate-400 text-slate-500">
            Submit mortgage leads and earn 5,000 credits per lead
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg dark:bg-slate-800 bg-slate-100 dark:text-slate-300 text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Users}
          label="Total Leads Submitted"
          value={stats.totalLeads}
          color="sky"
        />
        <StatCard
          icon={Coins}
          label="Total Credits Earned"
          value={stats.creditsEarned.toLocaleString()}
          color="emerald"
        />
        <StatCard
          icon={TrendingUp}
          label="This Month"
          value={stats.thisMonth}
          subtext="leads submitted"
          color="violet"
        />
        <StatCard
          icon={Gift}
          label="Monthly Earnings"
          value={(stats.thisMonth * 5000 + getAchievedMilestones(stats.thisMonth).reduce((sum, m) => sum + m.bonus, 0)).toLocaleString()}
          subtext="credits + bonuses"
          color="amber"
        />
      </div>

      {/* Milestone Progress */}
      <MilestoneProgress currentLeads={stats.thisMonth} />

      {/* Success/Error Messages */}
      {success && (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
          <p className="text-emerald-600 dark:text-emerald-400">{success}</p>
        </div>
      )}
      {error && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Lead Submission Form - Multi-Step */}
      <div className="rounded-2xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white overflow-hidden">
        {/* Form Header */}
        <div className="p-6 bg-emerald-500">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                {formStep === 1 ? 'Submit a Lead' : 'Loan Details'}
              </h2>
              <p className="text-emerald-100 text-sm">Step {formStep} / 2</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
              <Send className="h-5 w-5 text-white" />
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-4 flex gap-2">
            <div className={`h-1.5 flex-1 rounded-full ${formStep >= 1 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`h-1.5 flex-1 rounded-full ${formStep >= 2 ? 'bg-white' : 'bg-white/30'}`} />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Step 1: Contact Information */}
          {formStep === 1 && (
            <div className="space-y-5">
              <p className="text-sm dark:text-slate-400 text-slate-500 mb-4">
                Enter borrower contact information to get started.
              </p>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  className={`w-full px-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    formErrors.fullName ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                  }`}
                />
                {formErrors.fullName && <p className="mt-1 text-sm text-red-500">{formErrors.fullName}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="email@example.com"
                  className={`w-full px-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    formErrors.email ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                  }`}
                />
                {formErrors.email && <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  className={`w-full px-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    formErrors.phone ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                  }`}
                />
                {formErrors.phone && <p className="mt-1 text-sm text-red-500">{formErrors.phone}</p>}
              </div>

              {/* Next Button */}
              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors"
                >
                  Next
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Loan Details */}
          {formStep === 2 && (
            <div className="space-y-5">
              {/* Occupancy Type */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Occupancy Type
                </label>
                <select
                  name="occupancyType"
                  value={formData.occupancyType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                >
                  <option value="primary">Primary Residence</option>
                  <option value="secondary">Secondary Home</option>
                  <option value="investment">Investment Property</option>
                </select>
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Property Type
                </label>
                <select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                >
                  <option value="single_family">Single Family</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family (2-4 units)</option>
                  <option value="manufactured">Manufactured Home</option>
                </select>
              </div>

              {/* Loan Purpose */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Loan Purpose
                </label>
                <select
                  name="loanPurpose"
                  value={formData.loanPurpose}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                >
                  <option value="purchase">Purchase</option>
                  <option value="refinance">Refinance</option>
                  <option value="cash_out">Cash-Out Refinance</option>
                  <option value="heloc">HELOC</option>
                </select>
              </div>

              {/* Purchase Price */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Purchase Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <input
                    type="text"
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={(e) => handlePurchasePriceChange(e.target.value)}
                    placeholder="500,000"
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                      formErrors.purchasePrice ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                    }`}
                  />
                </div>
                {formErrors.purchasePrice && <p className="mt-1 text-sm text-red-500">{formErrors.purchasePrice}</p>}
              </div>

              {/* Down Payment */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Down Payment
                </label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <input
                      type="text"
                      name="downPayment"
                      value={formData.downPayment}
                      onChange={(e) => handleDownPaymentChange(e.target.value)}
                      placeholder="100,000"
                      className="w-full pl-8 pr-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="relative w-24">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.downPaymentPercent}
                      onChange={(e) => handleDownPaymentPercentChange(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                  </div>
                </div>
              </div>

              {/* City, State, Zip */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Los Angeles"
                    className={`w-full px-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                      formErrors.city ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                    }`}
                  />
                  {formErrors.city && <p className="mt-1 text-sm text-red-500">{formErrors.city}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="California"
                    className={`w-full px-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                      formErrors.state ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                    }`}
                  />
                  {formErrors.state && <p className="mt-1 text-sm text-red-500">{formErrors.state}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Zip Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="90210"
                  className={`w-full px-4 py-3 rounded-xl border dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    formErrors.zipCode ? 'border-red-500' : 'dark:border-slate-700 border-slate-200'
                  }`}
                />
                {formErrors.zipCode && <p className="mt-1 text-sm text-red-500">{formErrors.zipCode}</p>}
              </div>

              {/* Credit Score */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Credit Score Range
                </label>
                <select
                  name="creditScore"
                  value={formData.creditScore}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                >
                  <option value="760+">760+</option>
                  <option value="740-759">740 - 759</option>
                  <option value="720-739">720 - 739</option>
                  <option value="700-719">700 - 719</option>
                  <option value="680-699">680 - 699</option>
                  <option value="660-679">660 - 679</option>
                  <option value="640-659">640 - 659</option>
                  <option value="620-639">620 - 639</option>
                  <option value="below-620">Below 620</option>
                </select>
              </div>

              {/* Military/Veteran */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Military or Veteran?
                </label>
                <select
                  name="isVeteran"
                  value={formData.isVeteran}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold dark:text-slate-300 text-slate-700 mb-1.5">
                  Additional Notes (Optional)
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information about the borrower..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              {/* Navigation Buttons */}
              <div className="pt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl border dark:border-slate-600 border-slate-300 dark:text-slate-300 text-slate-700 font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 text-white font-semibold transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      Submit Lead
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Submitted Leads Table */}
      <div className="rounded-2xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white overflow-hidden">
        <div className="p-6 border-b dark:border-slate-700 border-slate-200">
          <h2 className="text-lg font-semibold dark:text-white text-slate-900">
            Your Submitted Leads
          </h2>
          <p className="text-sm dark:text-slate-400 text-slate-500">
            Track all the leads you've submitted and credits earned
          </p>
        </div>

        {leads.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 mx-auto dark:text-slate-600 text-slate-300 mb-4" />
            <p className="dark:text-slate-400 text-slate-500 font-medium">No leads submitted yet</p>
            <p className="text-sm dark:text-slate-500 text-slate-400 mt-1">
              Submit your first lead above to start earning credits
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="dark:bg-slate-900/50 bg-slate-50">
                <tr className="text-left text-sm dark:text-slate-400 text-slate-500">
                  <th className="px-4 py-3 font-medium">Borrower</th>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 font-medium">Loan Amount</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium">Credits</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700 divide-slate-200">
                {leads.map((lead) => (
                  <LeadRow key={lead.id} lead={lead} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="rounded-xl dark:bg-slate-800/30 bg-slate-50 p-4 text-center">
        <p className="text-sm dark:text-slate-400 text-slate-500">
          Duplicate leads (same borrower email) will not be credited.
          Credits are added instantly upon valid lead submission.
        </p>
      </div>
    </div>
  );
};

export default ReferAndEarn;
